import { useRef } from "react";

/**
 * Idempotency keys for a form's POSTs (P0-06): one key per attempt to send the same thing. A
 * retry of the same payload (after a timeout, say) reuses its key, so the API does the work
 * once. A different payload, or a new submit after the last one succeeded, gets a new key.
 */
export class IdempotencyKeys {
  private pending: { payload: string; key: string } | null = null;

  constructor(private readonly newKey: () => string = () => crypto.randomUUID()) {}

  keyFor(payload: unknown): string {
    const serialised = JSON.stringify(payload ?? null);
    if (this.pending?.payload !== serialised) this.pending = { payload: serialised, key: this.newKey() };
    return this.pending.key;
  }

  /** The request got an answer the API recorded (success or a definite refusal): the next submit is new. */
  settled(): void {
    this.pending = null;
  }
}

/** The form's IdempotencyKeys, kept for its lifetime. */
export function useIdempotencyKeys(): IdempotencyKeys {
  const keys = useRef<IdempotencyKeys | null>(null);
  keys.current ??= new IdempotencyKeys();
  return keys.current;
}
